import React from 'react';

import { truncate } from '@nfctron/nexus-core';
import { type ExtendedComponent, cn } from '@nfctron/nexus-react';

import { ActionIcon, Text, Tooltip } from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import Image from 'next/image';
import { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { type Swiper as SwiperClass } from 'swiper/types';

import { type Component } from '../../types/components';

import { type GalleryProps } from './Gallery.types';

/**
 * Gallery component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const Gallery: Component<GalleryProps> = ({ gallery, className, height = 200, controls = true, ...props }) => {
	const [swiper, setSwiper] = React.useState<SwiperClass | null>(null);
	const [activeIndex, setActiveIndex] = React.useState(0);

	/** enabled keyboard arrow navigation */
	useHotkeys([
		['ArrowLeft', () => swiper?.slidePrev()],
		['ArrowRight', () => swiper?.slideNext()],
	]);

	/** show no gallery without images */
	if (gallery.images.length === 0) return null;

	return (
		<div
			className={cn([
				'group/container relative overflow-hidden rounded-md border border-gray-200 bg-gray-100 transition-all duration-300 ease-out',
				className,
			])}
			{...props}
		>
			{/* left arrow */}
			{controls && gallery.images.length > 1 && (
				<React.Fragment>
					{/* left side */}
					{/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
					<GallerySide side="left" onClick={() => swiper?.slidePrev()} />

					{/* dots menu */}
					<div className="absolute bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center rounded-full bg-white p-1 opacity-0 transition-all duration-300 ease-out group-hover/container:opacity-90 hover:opacity-100!">
						{gallery.images.map((image, index) => (
							<Tooltip
								key={image.imageId}
								label={
									<Text fw={700} size="xs" c="purple-heart">
										{truncate(image.name, 48)}
									</Text>
								}
								bg="white"
							>
								<ActionIcon size="xs" className="bg-white hover:bg-slate-100" onClick={() => swiper?.slideTo(index)}>
									<span
										className={cn([
											'transition-all',
											index !== activeIndex && 'text-xl text-gray-400',
											index === activeIndex && 'text-purple-heart-500 text-2xl',
										])}
									>
										•
									</span>
								</ActionIcon>
							</Tooltip>
						))}
					</div>

					{/* right side */}
					{/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
					<GallerySide side="right" onClick={() => swiper?.slideNext()} />
				</React.Fragment>
			)}

			{/* content */}
			<Swiper
				rewind
				slidesPerView={1}
				onSwiper={(_swiper) => setSwiper(_swiper)}
				onSlideChange={(_swiper) => setActiveIndex(_swiper.activeIndex)}
				className="w-full"
				style={{ height, maxHeight: '128px' }}
				autoplay={{
					pauseOnMouseEnter: true,
					delay: 15000,
				}}
				modules={[Autoplay]}
			>
				{gallery.images.map((image, index) => (
					<SwiperSlide key={image.imageId} className="relative flex flex-col">
						{/* main foreground image */}
						<Image
							className="z-10"
							src={image.imageUrl}
							alt={image.description ?? image.name}
							layout="fill"
							objectFit="contain"
							{...(height < 100 && { objectFit: 'cover' })}
						/>
						{/* background blurred fill image */}
						<Image
							className="scale-125 opacity-50 blur-xl"
							src={image.imageUrl}
							alt={image.description ?? image.name}
							layout="fill"
							objectFit="cover"
							objectPosition="center"
						/>
					</SwiperSlide>
				))}
			</Swiper>
		</div>
	);
};

/**
 * Gallery/Side component
 * @constructor
 */
const GallerySide: React.FunctionComponent<ExtendedComponent<{ side: 'left' | 'right'; onClick: () => void }>> = ({
	side,
	onClick,
	className,
	...props
}) => {
	const Icon = side === 'left' ? ChevronLeftIcon : ChevronRightIcon;
	return (
		<div
			className={cn(['group absolute top-0 bottom-0 z-50 cursor-pointer', side === 'left' && 'left-0', side === 'right' && 'right-0', className])}
			{...props}
		>
			{/* button */}
			<button
				type="button"
				className={cn([
					'relative z-10 flex h-full min-w-24 items-center px-2 transition',
					side === 'left' && 'justify-start',
					side === 'right' && 'justify-end',
				])}
				onClick={onClick}
			>
				<Icon className="text-4xl text-white/25 transition-all group-hover:text-white" />
			</button>
			{/* background */}
			<div
				className={cn([
					'absolute inset-0 from-black/40 to-transparent opacity-0 transition-all duration-300 ease-out group-hover:opacity-100',
					side === 'left' && 'bg-linear-to-r',
					side === 'right' && 'bg-linear-to-l',
				])}
			/>
		</div>
	);
};

/**
 * Gallery component display name
 * @type {ComponentDisplayName}
 */
Gallery.displayName = 'component/Gallery';
